import { useEffect } from "react";
import { useParams } from "react-router-dom";

/**
 * QRRedirectShim — the tiny front-end route that catches
 * `linkola.app/q/{code}` and hard-navigates to `/api/q/{code}` so the
 * backend can 302-redirect to the current destination page.
 *
 * We use `window.location.replace` (NOT React Router) so the browser
 * follows the eventual HTTP redirect natively and preserves the "back"
 * button behavior: the user's history entry is replaced, not pushed.
 */
export default function QRRedirectShim() {
  const { code } = useParams();
  useEffect(() => {
    if (!code) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const safe = String(code).replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 12);
    // Use the same-origin path when we can (frontend and backend share the
    // ingress host in most deployments), otherwise fall back to the
    // absolute backend URL from env.
    // v149.90 — keep the query string: a QR on a Smart Screen carries
    // `?ss=<booking>` so its scans are credited to that promotion.
    const search = new URLSearchParams(window.location.search);
    const ss = (search.get("ss") || "").replace(/[^a-z0-9_]/gi, "").slice(0, 20);
    const qs = ss ? `?ss=${ss}` : "";
    const target = backend
      ? `${backend.replace(/\/$/, "")}/api/q/${safe}${qs}`
      : `/api/q/${safe}${qs}`;
    window.location.replace(target);
  }, [code]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream" data-testid="qr-redirect-shim">
      <div className="border-2 border-ink bg-white p-6 shadow-brutalSm text-center">
        <img src="/linkola-icon.png" alt="Linkola" className="w-14 h-14 mx-auto mb-3 object-contain" />
        <p className="font-display font-black text-lg tracking-tighter">Opening…</p>
        <p className="text-xs text-ink/50 font-mono mt-1">{code}</p>
      </div>
    </div>
  );
}
